import {
    Autocomplete,
    Button,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControl,
    Grid,
    InputLabel,
    MenuItem,
    Select,
    TextField,
} from "@mui/material";
import TreeSelect from "../../../components/TreeSelect";

const ENTITY_TYPES = ["CATEGORY", "PRODUCT", "SKU"];

export default function DiscountFormContainer({
    open,
    form,
    stores,
    isEditMode,
    onChange,
    onSave,
    onReset,
    onClose,
    entityOptions,
    loading
}) {
    const handleStoreChange = (storeId) => {
        onChange("storeId", storeId);
        onChange("entityId", "");
    };

    const handleEntityTypeChange = (event) => {
        onChange("entityType", event.target.value);
        onChange("entityId", "");
    };

    const renderEntityIdField = () => {
        return (
            <FormControl fullWidth size="small" disabled={!form.entityType || !form.storeId || loading}>
                {form.entityType === "CATEGORY" ? (
                    <TreeSelect
                        labelId="discount-form-entity-id-label"
                        label="Entity ID *"
                        value={form.entityId}
                        options={entityOptions}
                        onChange={(event) => !form.id && onChange("entityId", event.target.value)}
                    />
                ) : (
                    <Autocomplete
                        readOnly={Boolean(form?.id)}
                        size="small"
                        options={entityOptions}
                        value={entityOptions.find((option) => String(option.id) === String(form.entityId)) || null}
                        isOptionEqualToValue={(option, selected) =>
                            String(option.id) === String(selected.id)
                        }
                        getOptionLabel={(option) => `#${option.id} - ${option.name}`}
                        onChange={(_, selectedOption) => {
                            !form.id &&
                                onChange(
                                    "entityId",
                                    selectedOption ? String(selectedOption.id) : ""
                                );
                        }}
                        renderInput={(params) => <TextField {...params} label="Entity ID " required />}
                    />
                )}
            </FormControl>
        );
    }

    const disabled = !form.storeId || !form.entityType || !form.entityId || !form.discountPct || loading;
    const isDiscountHigh = form.discountPct > 30;

    return (
        <Dialog open={open}
            onClose={(_, reason) => {
                if (reason === "backdropClick") return;
                onClose();
            }}
            fullWidth
            maxWidth="lg"
            hideBackdrop={false}
        >
            <DialogTitle>
                {isEditMode ? "Update Liquidation Sale Discount" : "Create Liquidation Sale Discount"}
            </DialogTitle>

            <DialogContent dividers>
                <Grid container spacing={2} sx={{ mt: 0.5 }}>
                    <Grid item size={{ xs: 12, md: 3 }}>
                        <Autocomplete
                            readOnly={Boolean(form?.id)}
                            fullWidth
                            size="small"
                            options={stores}
                            value={stores.find((s) => String(s.storeId) === String(form.storeId)) || null}
                            isOptionEqualToValue={(option, selected) =>
                                String(option.storeId) === String(selected.storeId)
                            }
                            getOptionLabel={(option) => `${option?.storeId} - ${option?.storeName || ""}`}
                            onChange={(_, selectedStore) => !form.id && handleStoreChange(selectedStore?.storeId ?? "")}

                            renderInput={(params) => <TextField {...params} label="Store" required />}
                        />
                    </Grid>

                    <Grid item size={{ xs: 12, md: 3 }}>
                        <FormControl fullWidth size="small">
                            {form.id ?
                                <TextField
                                    fullWidth
                                    size="small"
                                    label="Entity Type "
                                    value={form.entityType}
                                    slotProps={{ readOnly: true }}
                                    required
                                />
                                :
                                <>
                                    <InputLabel id="discount-form-entity-type-label">Entity Type</InputLabel>
                                    <Select
                                        labelId="discount-form-entity-type-label"
                                        value={form.entityType}
                                        label="Entity Type *"
                                        onChange={(e) => !form?.id && handleEntityTypeChange(e)}
                                        required
                                    >
                                        {ENTITY_TYPES.map((type) => (
                                            <MenuItem key={type} value={type}>
                                                {type}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </>
                            }
                        </FormControl>
                    </Grid>

                    <Grid item size={{ xs: 12, md: 3 }}>
                        {renderEntityIdField()}
                    </Grid>

                    <Grid item size={{ xs: 12, md: 3 }}>
                        <TextField
                            fullWidth
                            size="small"
                            label="Discount % "
                            type="number"
                            value={form.discountPct}
                            onChange={(event) => (event.target.value < 0 || event.target.value > 100) ? null : onChange("discountPct", event.target.value)}
                            slotProps={{ input: { min: 0, max: 100 } }}
                            error={!form.discountPct}
                            color={isDiscountHigh ? "warning" : "primary"}
                            sx={isDiscountHigh ? {
                                '& .MuiOutlinedInput-root': {
                                    '& .MuiOutlinedInput-notchedOutline': {
                                        borderColor: 'warning.main',
                                    },
                                    '&:hover .MuiOutlinedInput-notchedOutline': {
                                        borderColor: 'warning.dark',
                                    },
                                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                        borderColor: 'warning.main',
                                    },
                                },
                                '& .MuiInputLabel-root': {
                                    color: 'warning.main',
                                },
                                '& .MuiInputLabel-root.Mui-focused': {
                                    color: 'warning.main',
                                },
                            } : undefined}
                            required
                        />
                    </Grid>
                </Grid>
            </DialogContent>

            <DialogActions sx={{ px: 3, py: 2 }}>
                <Button
                    variant="outlined"
                    onClick={() => {
                        onReset();
                        onClose();
                    }}
                >
                    Cancel
                </Button>

                {!form.id && (
                    <Button variant="text" onClick={onReset}>
                        Reset
                    </Button>
                )}

                <Button variant="contained" onClick={onSave} disabled={disabled}>
                    {isEditMode ? "Update Discount" : "Save Discount"}
                </Button>
            </DialogActions>
        </Dialog>
    );
}
